import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { UserCog, Plus, Link2, Mail, Shield, User } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "client"
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, clientsRes] = await Promise.all([
        axios.get(`${API_URL}/users`),
        axios.get(`${API_URL}/clients`)
      ]);
      setUsers(usersRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/auth/register`, formData);
      toast.success("Utilizador criado com sucesso");
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar utilizador");
    }
  };

  const handleAssignClient = async () => {
    if (!selectedUser || !selectedClientId) return;
    try {
      await axios.put(`${API_URL}/users/${selectedUser.id}/assign-client/${selectedClientId}`);
      toast.success("Cliente atribuído com sucesso");
      setAssignDialogOpen(false);
      setSelectedUser(null);
      setSelectedClientId("");
      fetchData();
    } catch (error) {
      toast.error("Erro ao atribuir cliente");
    }
  };

  const resetForm = () => {
    setFormData({ name: "", email: "", password: "", role: "client" });
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? `${client.company}` : "-";
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="skeleton h-8 w-48" />
        <Card><CardContent className="pt-6"><div className="skeleton h-64" /></CardContent></Card>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="admin-users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3" style={{ fontFamily: 'Manrope' }}>
            <UserCog className="w-8 h-8 text-blue-600" />
            Gestão de Utilizadores
          </h1>
          <p className="text-slate-500 mt-1">
            Gerir utilizadores e atribuir clientes
          </p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-user-btn">
              <Plus className="w-4 h-4 mr-2" />
              Novo Utilizador
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope' }}>
                Novo Utilizador
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nome</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Nome completo"
                  required
                  data-testid="user-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@exemplo.pt"
                  required
                  data-testid="user-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  required
                  data-testid="user-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Tipo de Utilizador</Label>
                <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="client">Cliente</SelectItem>
                    <SelectItem value="admin">Administrador</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="save-user-btn">
                  Criar Utilizador
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Assign Client Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              Atribuir Cliente
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-slate-600">
              Selecione o cliente para atribuir a <strong>{selectedUser?.name}</strong>
            </p>
            <Select value={selectedClientId} onValueChange={setSelectedClientId}>
              <SelectTrigger data-testid="assign-client-select">
                <SelectValue placeholder="Selecione um cliente" />
              </SelectTrigger>
              <SelectContent>
                {clients.map((client) => (
                  <SelectItem key={client.id} value={client.id}>
                    {client.company} - {client.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setAssignDialogOpen(false)} className="flex-1">
                Cancelar
              </Button>
              <Button onClick={handleAssignClient} className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="confirm-assign-btn">
                Atribuir
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4 bg-blue-50 border-blue-100">
          <div className="text-blue-600 text-sm font-medium">Total Utilizadores</div>
          <div className="text-2xl font-bold text-blue-900 mt-1" style={{ fontFamily: 'Manrope' }}>
            {users.length}
          </div>
        </Card>
        <Card className="p-4 bg-purple-50 border-purple-100">
          <div className="text-purple-600 text-sm font-medium">Administradores</div>
          <div className="text-2xl font-bold text-purple-900 mt-1" style={{ fontFamily: 'Manrope' }}>
            {users.filter(u => u.role === "admin").length}
          </div>
        </Card>
        <Card className="p-4 bg-emerald-50 border-emerald-100">
          <div className="text-emerald-600 text-sm font-medium">Clientes</div>
          <div className="text-2xl font-bold text-emerald-900 mt-1" style={{ fontFamily: 'Manrope' }}>
            {users.filter(u => u.role === "client").length}
          </div>
        </Card>
        <Card className="p-4 bg-amber-50 border-amber-100">
          <div className="text-amber-600 text-sm font-medium">Com Cliente Atribuído</div>
          <div className="text-2xl font-bold text-amber-900 mt-1" style={{ fontFamily: 'Manrope' }}>
            {users.filter(u => u.client_id).length}
          </div>
        </Card>
      </div>

      {/* Users Table */}
      <Card className="stat-card">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
            Lista de Utilizadores
          </CardTitle>
        </CardHeader>
        <CardContent>
          {users.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Cliente Atribuído</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                            <span className="text-blue-600 font-semibold text-sm">
                              {user.name?.charAt(0)?.toUpperCase() || "U"}
                            </span>
                          </div>
                          {user.name}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-slate-400" />
                          {user.email}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={user.role === "admin" ? "badge-info" : "badge-success"}>
                          <Shield className="w-3 h-3 mr-1" />
                          {user.role === "admin" ? "Admin" : "Cliente"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {user.client_id ? (
                          <Badge variant="outline" className="badge-success">
                            {getClientName(user.client_id)}
                          </Badge>
                        ) : (
                          <span className="text-slate-400">Não atribuído</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {user.role === "client" && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedUser(user);
                              setSelectedClientId(user.client_id || "");
                              setAssignDialogOpen(true);
                            }}
                            data-testid={`assign-client-${user.id}`}
                          >
                            <Link2 className="w-4 h-4 mr-1" />
                            Atribuir Cliente
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12 text-slate-400">
              <User className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Ainda não existem utilizadores registados</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminUsersPage;
